import React, { useEffect, useState, useCallback } from 'react';
import { Oval } from 'react-loader-spinner';
import { FaTimes, FaMapMarkerAlt, FaBriefcase, FaClock, FaBuilding } from 'react-icons/fa';
import api from '../services/api';
import LazyImage from '../components/LazyImage';
import {
  Page, Header, HeaderCard, HeaderImage, Section,
  JobTable, MobileJobList, MobileJobCard, MobileJobMeta,
  Badge, Overlay, Modal, ModalHeader, CloseButton, ModalBody,
  DetailRow, DetailList, Divider, FormGroup, ApplyButton,
  SuccessMessage, ErrorText
} from './CareerStyles';

const formatDate = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const isExpired = (deadline) => {
  if (!deadline) return false;
  return new Date(deadline) < new Date();
};

const Careers = () => {
  const [page, setPage] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [showApplyForm, setShowApplyForm] = useState(false);
  const [form, setForm] = useState({ applicantName: '', applicantEmail: '', applicantPhone: '', coverLetter: '', resume: '', resumeFileName: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [p, j] = await Promise.all([api.careers.getPage(), api.careers.listJobs()]);
        setPage(p);
        setJobs(j.filter(job => job.isActive));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const openJob = useCallback((job) => {
    setSelected(job);
    setShowApplyForm(false);
    setSubmitResult(null);
    setSubmitError(null);
    setForm({ applicantName: '', applicantEmail: '', applicantPhone: '', coverLetter: '', resume: '', resumeFileName: '' });
  }, []);

  const closeModal = useCallback(() => {
    setSelected(null);
    setShowApplyForm(false);
    setSubmitResult(null);
    setSubmitError(null);
  }, []);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleResumeChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setSubmitError('Resume file must be under 10MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setForm(prev => ({ ...prev, resume: reader.result, resumeFileName: file.name }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    if (!form.applicantName.trim() || !form.applicantEmail.trim()) {
      setSubmitError('Name and email are required');
      return;
    }
    setSubmitting(true);
    try {
      await api.careers.submitApplication({ ...form, jobId: selected._id });
      setSubmitResult(true);
      setShowApplyForm(false);
    } catch (err) {
      setSubmitError(err.message || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Oval height={48} width={48} color="#2E8B57" secondaryColor="#CDEAD7" />
    </div>
  );

  if (error) return <div style={{ padding: 20 }}>Error: {error}</div>;

  const expired = selected ? isExpired(selected.deadline) : false;

  return (
    <Page>
      <Header>
        <HeaderCard>
          <h1>{page?.title || 'Careers'}</h1>
          <p style={{ whiteSpace: 'pre-wrap' }}>{page?.description || 'Join our team and make a difference in food safety across Liberia.'}</p>
        </HeaderCard>
        <HeaderImage>
          {page?.headerImage ? <LazyImage src={page.headerImage} alt="Careers" /> : null}
        </HeaderImage>
      </Header>

      <Section>
        <h2 style={{ color: '#2E8B57', marginBottom: 20 }}>Open Positions</h2>
        {jobs.length === 0 ? (
          <p style={{ color: '#6b7280' }}>No current openings. Please check back later.</p>
        ) : (
          <>
            <JobTable>
              <thead>
                <tr>
                  <th>Position</th>
                  <th>Department</th>
                  <th>Type</th>
                  <th>Location</th>
                  <th>Deadline</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map(job => (
                  <tr key={job._id} onClick={() => openJob(job)}>
                    <td style={{ fontWeight: 600, color: '#15803d' }}>{job.title}</td>
                    <td>{job.department || '—'}</td>
                    <td><Badge>{job.type}</Badge></td>
                    <td>{job.location}</td>
                    <td>{job.deadline ? formatDate(job.deadline) : '—'}</td>
                    <td>
                      {isExpired(job.deadline)
                        ? <Badge $variant="red">Closed</Badge>
                        : <Badge>Open</Badge>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </JobTable>

            <MobileJobList>
              {jobs.map(job => (
                <MobileJobCard key={job._id} onClick={() => openJob(job)}>
                  <h3>{job.title}</h3>
                  <MobileJobMeta>
                    <Badge>{job.type}</Badge>
                    {job.department && <span>{job.department}</span>}
                    <span>{job.location}</span>
                    {job.deadline && <span>Deadline: {formatDate(job.deadline)}</span>}
                  </MobileJobMeta>
                </MobileJobCard>
              ))}
            </MobileJobList>
          </>
        )}
      </Section>

      {selected && (
        <Overlay onClick={closeModal}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h2>{selected.title}</h2>
              <CloseButton onClick={closeModal}><FaTimes /></CloseButton>
            </ModalHeader>
            <ModalBody>
              <DetailRow>
                {selected.department && (
                  <Badge $variant="orange"><FaBuilding style={{ marginRight: 4 }} />{selected.department}</Badge>
                )}
                <Badge><FaBriefcase style={{ marginRight: 4 }} />{selected.type}</Badge>
                <Badge><FaMapMarkerAlt style={{ marginRight: 4 }} />{selected.location}</Badge>
                {selected.deadline && (
                  <Badge $variant={expired ? 'red' : undefined}>
                    <FaClock style={{ marginRight: 4 }} />
                    {expired ? 'Closed' : `Deadline: ${formatDate(selected.deadline)}`}
                  </Badge>
                )}
              </DetailRow>

              <p style={{ lineHeight: 1.7, color: '#374151', whiteSpace: 'pre-wrap' }}>{selected.description}</p>

              {selected.responsibilities?.length > 0 && (
                <>
                  <h3 style={{ margin: '16px 0 8px', color: '#15803d' }}>Responsibilities</h3>
                  <DetailList>
                    {selected.responsibilities.map((r, i) => <li key={i}>{r}</li>)}
                  </DetailList>
                </>
              )}

              {selected.requirements?.length > 0 && (
                <>
                  <h3 style={{ margin: '16px 0 8px', color: '#15803d' }}>Requirements</h3>
                  <DetailList>
                    {selected.requirements.map((r, i) => <li key={i}>{r}</li>)}
                  </DetailList>
                </>
              )}

              <Divider />

              {submitResult ? (
                <SuccessMessage>
                  <strong>Application submitted!</strong>
                  Your application for {selected.title} has been received. We will review it and get back to you.
                </SuccessMessage>
              ) : expired ? (
                <div style={{ textAlign: 'center', color: '#6b7280', padding: '10px 0' }}>
                  This position is no longer accepting applications.
                </div>
              ) : !showApplyForm ? (
                <ApplyButton onClick={() => setShowApplyForm(true)}>
                  Apply for this Position
                </ApplyButton>
              ) : (
                <form onSubmit={handleSubmit}>
                  <h3 style={{ margin: '0 0 14px', color: '#15803d' }}>Application Form</h3>
                  <FormGroup>
                    <label htmlFor="apply-name">Full Name *</label>
                    <input id="apply-name" name="applicantName" value={form.applicantName} onChange={handleFormChange} required />
                  </FormGroup>
                  <FormGroup>
                    <label htmlFor="apply-email">Email Address *</label>
                    <input id="apply-email" name="applicantEmail" type="email" value={form.applicantEmail} onChange={handleFormChange} required />
                  </FormGroup>
                  <FormGroup>
                    <label htmlFor="apply-phone">Phone Number</label>
                    <input id="apply-phone" name="applicantPhone" value={form.applicantPhone} onChange={handleFormChange} />
                  </FormGroup>
                  <FormGroup>
                    <label htmlFor="apply-cover">Cover Letter</label>
                    <textarea id="apply-cover" name="coverLetter" value={form.coverLetter} onChange={handleFormChange} placeholder="Tell us why you're a good fit for this role..." />
                  </FormGroup>
                  <FormGroup>
                    <label htmlFor="apply-resume">Resume / CV (PDF, max 10MB)</label>
                    <input id="apply-resume" type="file" accept=".pdf,.doc,.docx" onChange={handleResumeChange} />
                    {form.resumeFileName && <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: 4 }}>Selected: {form.resumeFileName}</div>}
                  </FormGroup>
                  {submitError && <ErrorText>{submitError}</ErrorText>}
                  <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                    <ApplyButton type="submit" disabled={submitting}>
                      {submitting ? 'Submitting...' : 'Submit Application'}
                    </ApplyButton>
                  </div>
                </form>
              )}
            </ModalBody>
          </Modal>
        </Overlay>
      )}
    </Page>
  );
};

export default Careers;
